import { useLayoutEffect, type RefObject } from 'react';
import type { MenuItem, MenuVariant } from './Menu';
import {
  applyContainerAlignment,
  applyMetricsStyles,
  applyThirdLevelStyles,
  applyTypoStyles,
  findPathToActiveItem,
} from './menuHelpers';

type ExpandableMenuItemElement = HTMLElement & { isOpen?: boolean };

const findMenuItemElement = (menuRef: RefObject<HTMLDivElement | null>, id: string) => {
  return menuRef.current?.querySelector(
    `[data-test="menu-item-${id}"]`,
  ) as ExpandableMenuItemElement | null;
};

const ensureItemIsExpanded = (itemElement: ExpandableMenuItemElement) => {
  if (itemElement.isOpen === false) {
    itemElement.isOpen = true;
  }

  if (!itemElement.hasAttribute('is-open')) {
    itemElement.setAttribute('is-open', 'true');
  }
};

const expandItemsFromPathIds = (menuRef: RefObject<HTMLDivElement | null>, pathIds: string[]) => {
  pathIds.forEach((id) => {
    const itemElement = findMenuItemElement(menuRef, id);
    if (itemElement) {
      ensureItemIsExpanded(itemElement);
    }
  });
};

const expandActiveItem = (
  menuRef: RefObject<HTMLDivElement | null>,
  visibleItems: MenuItem[],
  activeItemPath: string | null,
) => {
  const currentPath = activeItemPath ?? globalThis.location.pathname;
  const activePathIds = findPathToActiveItem(visibleItems, currentPath);

  if (activePathIds) {
    expandItemsFromPathIds(menuRef, activePathIds);
  }
};

const applyShadowStyles = (menuRef: RefObject<HTMLDivElement | null>, variant: MenuVariant) => {
  const navItems = menuRef.current?.querySelectorAll('bds-nav-tree-item');
  navItems?.forEach((item: any) => {
    if (!item.shadowRoot) return;
    applyTypoStyles(item);
    if (variant === 'metrics') {
      applyMetricsStyles(item);
    } else {
      applyThirdLevelStyles(item);
    }
    applyContainerAlignment(item);
  });
};

export const useMenuShadowEffects = (
  menuRef: RefObject<HTMLDivElement | null>,
  visibleItems: MenuItem[],
  activeItemPath: string | null,
  variant: MenuVariant = 'default',
) => {
  useLayoutEffect(() => {
    const runEffects = () => {
      applyShadowStyles(menuRef, variant);
      expandActiveItem(menuRef, visibleItems, activeItemPath);
    };

    runEffects();
    const interval = setInterval(runEffects, 1000);
    const timeout = setTimeout(() => clearInterval(interval), 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [menuRef, visibleItems, activeItemPath, variant]);
};
